import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Users, Download, Send, Globe, Clock, DollarSign } from 'lucide-react';

const UpcomingConference = () => {
  const [activeTab, setActiveTab] = useState('home');

  const conferencesList = [
    {
      id: '218',
      name: "15th International Conference on Recent Challenges in Engineering & Technology",
      theme: "Recent Challenges in Engineering",
      mode: "Physical",
      date: "September 15-17, 2024",
      venue: "Grand Convention Center, Chennai, India",
      organizer: "ESN Publications"
    },
    {
      id: '219',
      name: "International Conference on Advanced Research in Science & Technology 2024",
      theme: "Innovation, Sustainability, and Future Technologies",
      mode: "Hybrid (Physical + Virtual)",
      date: "October 10-12, 2024",
      venue: "Grand Convention Center, Bangalore, India",
      organizer: "ESN Publications"
    }
  ];

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const paramId = params.get('id');

  const conferenceData = conferencesList.find(c => c.id === paramId) || conferencesList[0];

  const keynoteSpeakers = [
    {
      photo: "/images/speaker1.jpg",
      name: "Dr. Sarah Johnson",
      designation: "Professor of Computer Science",
      country: "USA"
    },
    {
      photo: "/images/speaker2.jpg",
      name: "Prof. Raj Patel",
      designation: "Director of AI Research",
      country: "India"
    },
    {
      photo: "/images/speaker3.jpg",
      name: "Dr. Maria Garcia",
      designation: "Head of Biotechnology",
      country: "Spain"
    },
    {
      photo: "/images/speaker4.jpg",
      name: "Prof. Chen Wei",
      designation: "Sustainable Technology Expert",
      country: "China"
    }
  ];

  const importantDates = [
    { event: "Early Bird Registration Deadline", date: "June 30, 2024" },
    { event: "Abstract Submission Deadline", date: "July 15, 2024" },
    { event: "Final Registration Deadline", date: "August 20, 2024" },
    { event: "Full Paper Submission Deadline", date: "August 30, 2024" }
  ];

  const renderTabContent = () => {
    switch(activeTab) {
      case 'home':
        return (
          <div>
            {/* Conference Overview */}
            <div style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              padding: '3px', 
              borderRadius: '20px', 
              marginBottom: '50px'
            }}>
              <div style={{ backgroundColor: 'white', padding: '50px', borderRadius: '17px' }}>
                <h2 style={{ color: '#2d3748', fontSize: '2.5rem', fontWeight: 700, marginBottom: '30px', textAlign: 'center' }}>
                  {conferenceData.name}
                </h2>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '30px', marginBottom: '40px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '15px' }}>🎯</div>
                    <h3 style={{ color: '#667eea', fontSize: '1.2rem', fontWeight: 700, marginBottom: '10px' }}>Theme</h3>
                    <p style={{ color: '#4a5568', fontWeight: 600 }}>{conferenceData.theme}</p>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '15px' }}>📅</div>
                    <h3 style={{ color: '#667eea', fontSize: '1.2rem', fontWeight: 700, marginBottom: '10px' }}>Date</h3>
                    <p style={{ color: '#4a5568', fontWeight: 600 }}>{conferenceData.date}</p>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '15px' }}>🌐</div>
                    <h3 style={{ color: '#667eea', fontSize: '1.2rem', fontWeight: 700, marginBottom: '10px' }}>Mode</h3>
                    <p style={{ color: '#4a5568', fontWeight: 600 }}>{conferenceData.mode}</p>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '15px' }}>📍</div>
                    <h3 style={{ color: '#667eea', fontSize: '1.2rem', fontWeight: 700, marginBottom: '10px' }}>Venue</h3>
                    <p style={{ color: '#4a5568', fontWeight: 600 }}>{conferenceData.venue}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* About & Objectives */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '40px' }}>
              <div style={{ background: '#f7fafc', padding: '40px', borderRadius: '16px', border: '2px solid #e2e8f0' }}>
                <h3 style={{ color: '#2d3748', fontSize: '1.8rem', fontWeight: 700, marginBottom: '25px' }}>About Conference</h3>
                <p style={{ color: '#4a5568', fontSize: '1.1rem', lineHeight: '1.7' }}>
                  This premier international conference brings together leading researchers, academicians, and industry professionals 
                  to discuss cutting-edge developments in science and technology. The event will feature keynote speeches, 
                  research presentations, panel discussions, and networking opportunities.
                </p>
              </div>
              
              <div style={{ background: '#f7fafc', padding: '40px', borderRadius: '16px', border: '2px solid #e2e8f0' }}>
                <h3 style={{ color: '#2d3748', fontSize: '1.8rem', fontWeight: 700, marginBottom: '25px' }}>Objectives</h3>
                <ul style={{ color: '#4a5568', fontSize: '1.1rem', lineHeight: '1.7', paddingLeft: '20px' }}>
                  <li>Foster collaboration between researchers worldwide</li>
                  <li>Share innovative research findings and methodologies</li>
                  <li>Discuss future trends in science and technology</li>
                  <li>Promote sustainable development practices</li>
                </ul>
              </div>
            </div>
          </div>
        );

      case 'speakers':
        return (
          <div>
            <h2 style={{ color: '#2d3748', fontSize: '2.5rem', fontWeight: 700, marginBottom: '50px', textAlign: 'center' }}>
              Keynote Speakers
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px' }}>
              {keynoteSpeakers.map((speaker, index) => (
                <div key={index} style={{ 
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
                  padding: '3px', 
                  borderRadius: '20px' 
                }}>
                  <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '17px', textAlign: 'center' }}>
                    <div style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: '50%',
                      backgroundColor: '#e2e8f0',
                      margin: '0 auto 25px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '3rem'
                    }}>
                      👨‍🎓
                    </div>
                    <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '10px' }}>
                      {speaker.name}
                    </h3>
                    <p style={{ color: '#667eea', fontSize: '1.1rem', fontWeight: 600, marginBottom: '8px' }}>
                      {speaker.designation}
                    </p>
                    <p style={{ color: '#718096', fontSize: '1rem', fontWeight: 600 }}>
                      {speaker.country}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'dates':
        return (
          <div>
            <h2 style={{ color: '#2d3748', fontSize: '2.5rem', fontWeight: 700, marginBottom: '50px', textAlign: 'center' }}>
              Important Dates
            </h2>
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
              {importantDates.map((item, index) => (
                <div key={index} style={{ 
                  background: index % 2 === 0 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                    : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
                  padding: '3px', 
                  borderRadius: '15px',
                  marginBottom: '20px'
                }}>
                  <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <Clock size={24} color="#667eea" />
                      <span style={{ color: '#2d3748', fontSize: '1.2rem', fontWeight: 600 }}>{item.event}</span>
                    </div>
                    <span style={{ color: '#667eea', fontSize: '1.1rem', fontWeight: 700 }}>{item.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'submission':
        return (
          <div>
            <h2 style={{ color: '#2d3748', fontSize: '2.5rem', fontWeight: 700, marginBottom: '50px', textAlign: 'center' }}>
              Paper Submission & Registration
            </h2>
            
            <div style={{ display: 'grid', gap: '40px' }}>
              {/* Publication Details */}
              <div style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                padding: '3px', 
                borderRadius: '20px' 
              }}>
                <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '17px' }}>
                  <h3 style={{ color: '#2d3748', fontSize: '1.8rem', fontWeight: 700, marginBottom: '25px' }}>Paper Publication Details</h3>
                  <ul style={{ color: '#4a5568', fontSize: '1.1rem', lineHeight: '1.8', paddingLeft: '20px' }}>
                    <li>All accepted papers will be published in conference proceedings</li>
                    <li>Proceedings will have ISBN and will be available online</li>
                    <li>Selected papers will be recommended for journal publication</li>
                    <li>Certificate of participation will be provided to all attendees</li>
                  </ul>
                </div>
              </div>

              {/* Fees Details */}
              <div style={{ 
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
                padding: '3px', 
                borderRadius: '20px' 
              }}>
                <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '17px' }}>
                  <h3 style={{ color: '#2d3748', fontSize: '1.8rem', fontWeight: 700, marginBottom: '25px' }}>Registration Fees</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '25px' }}>
                    <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f7fafc', borderRadius: '12px' }}>
                      <DollarSign size={32} color="#f5576c" style={{ margin: '0 auto 15px' }} />
                      <h4 style={{ color: '#2d3748', fontSize: '1.3rem', fontWeight: 700, marginBottom: '10px' }}>Indian Participants</h4>
                      <p style={{ color: '#f5576c', fontSize: '1.5rem', fontWeight: 700 }}>₹5,000</p>
                    </div>
                    <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f7fafc', borderRadius: '12px' }}>
                      <DollarSign size={32} color="#f5576c" style={{ margin: '0 auto 15px' }} />
                      <h4 style={{ color: '#2d3748', fontSize: '1.3rem', fontWeight: 700, marginBottom: '10px' }}>International Participants</h4>
                      <p style={{ color: '#f5576c', fontSize: '1.5rem', fontWeight: 700 }}>$150</p>
                    </div>
                    <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f7fafc', borderRadius: '12px' }}>
                      <DollarSign size={32} color="#f5576c" style={{ margin: '0 auto 15px' }} />
                      <h4 style={{ color: '#2d3748', fontSize: '1.3rem', fontWeight: 700, marginBottom: '10px' }}>Virtual Attendance</h4>
                      <p style={{ color: '#f5576c', fontSize: '1.5rem', fontWeight: 700 }}>₹2,500</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Guidelines */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '30px' }}>
                <div style={{ background: '#f7fafc', padding: '30px', borderRadius: '16px', border: '2px solid #e2e8f0' }}>
                  <h4 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '20px' }}>Paper Submission Guidelines</h4>
                  <ul style={{ color: '#4a5568', fontSize: '1rem', lineHeight: '1.6', paddingLeft: '20px' }}>
                    <li>Papers should be 6-8 pages in IEEE format</li>
                    <li>Submit in PDF format only</li>
                    <li>Include abstract, keywords, and references</li>
                    <li>Plagiarism check will be conducted</li>
                  </ul>
                </div>
                
                <div style={{ background: '#f7fafc', padding: '30px', borderRadius: '16px', border: '2px solid #e2e8f0' }}>
                  <h4 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '20px' }}>PPT Guidelines</h4>
                  <ul style={{ color: '#4a5568', fontSize: '1rem', lineHeight: '1.6', paddingLeft: '20px' }}>
                    <li>Maximum 15 slides for presentation</li>
                    <li>Include title, objectives, methodology, results</li>
                    <li>Time limit: 12 minutes + 3 minutes Q&A</li>
                    <li>Use clear fonts and high-quality images</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ padding: '40px 24px 0' }}>
        <div className="container-custom">
          <Link to="/conference" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#667eea', textDecoration: 'none', marginBottom: '30px', fontWeight: '600' }}>
            <ArrowLeft size={20} />
            <span>Back to Conferences</span>
          </Link>
          
          <div style={{ textAlign: 'center', marginBottom: '50px' }}>
            <div style={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '12px 30px',
              borderRadius: '25px',
              fontSize: '1rem',
              fontWeight: 'bold',
              marginBottom: '20px'
            }}>
              🚀 UPCOMING CONFERENCE
            </div>
            <h1 style={{ color: '#2d3748', fontSize: '3rem', fontWeight: 700, marginBottom: '20px' }}>Upcoming Conference</h1>
          </div>

          {/* Navigation Tabs */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '48px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {[
              { key: 'home', label: 'Home', icon: '🏠' },
              { key: 'speakers', label: 'Keynote Speakers', icon: '🎤' },
              { key: 'dates', label: 'Important Dates', icon: '📅' },
              { key: 'submission', label: 'Paper Submission', icon: '📄' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '12px 24px',
                  borderRadius: '25px',
                  border: activeTab === tab.key ? 'none' : '2px solid #667eea',
                  backgroundColor: activeTab === tab.key ? '#667eea' : 'transparent',
                  color: activeTab === tab.key ? 'white' : '#667eea',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="container-custom" style={{ padding: '0 24px 80px' }}>
        {renderTabContent()}
      </div>

      {/* Download Brochure Button */}
      <div style={{ position: 'fixed', bottom: '30px', right: '30px', zIndex: 1000 }}>
        <button style={{
          padding: '15px 25px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '30px',
          fontSize: '1rem',
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
          transition: 'transform 0.2s ease'
        }}
        onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
        onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}>
          <Download size={20} />
          Conference Brochure
        </button>
      </div>
    </div>
  );
};

export default UpcomingConference;